import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ScienceOutlined } from '@mui/icons-material';
import { systemService } from '../services';

/**
 * Shown across every page when the backend is running the stub SMS provider.
 *
 * The flag comes from the server, never from the frontend build mode: only the
 * backend knows which provider it actually constructed, and the two can
 * disagree. On a real production deployment this renders nothing.
 *
 * It is deliberately hard to miss. A simulated send produces the same green
 * result and the same Delivered row as a real one, so without this an admin (or
 * a client watching a demo) has no way to tell that nothing reached a phone.
 */
export const SimulationBanner: React.FC = () => {
  const [simulated, setSimulated] = useState(false);

  useEffect(() => {
    let active = true;
    systemService
      .getMode()
      .then((mode) => {
        if (active) setSimulated(mode.smsSimulated);
      })
      .catch(() => {
        // Health is unreachable, which the page will surface elsewhere. Staying
        // quiet is right here: claiming simulation mode when we do not know
        // would be worse than showing nothing.
      });
    return () => {
      active = false;
    };
  }, []);

  if (!simulated) return null;

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 0.75,
        px: 2,
        backgroundColor: 'warning.main',
        color: 'warning.contrastText',
      }}
    >
      <ScienceOutlined fontSize="small" />
      <Typography variant="body2" fontWeight={600} textAlign="center">
        Simulation mode: SMS are not delivered to real phones and nothing is charged.
      </Typography>
    </Box>
  );
};
